// @ts-nocheck
/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, List, ListItem, ListItemText, Box, TextField, Autocomplete, Typography, Tooltip, Chip } from '@mui/material';
import { useDispatch } from 'react-redux';
import Snackbar from '@mui/material/Snackbar';
import { useParams } from 'react-router-dom';
import MuiAlert from '@mui/material/Alert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DOMPurify from 'dompurify';
import TextEditor from './TextEditor';
import { addOrderNote, editOrderNote, deleteOrderNote } from 'actions/access';
import { teal } from 'api';
import { renderDateTime } from 'utils/helpers';
import { normalizeTaggedUsers, resolveNoteAuthorName, getNoteDate, getNoteId, collectAllTaggedUsers } from './PenlyAccessUserNotes.helpers';

export default function PenlyAccessUserNotes({ user, init, orderId }) {
    const [showEditor, setShowEditor] = useState(false);
    const [editingId, setEditingId] = useState(undefined);

    const dispatch = useDispatch();
    const { organisationId } = useParams();
    const [notes, setNotes] = useState([]);
    const [snack, setSnack] = useState();
    const [organisationUsers, setOrganisationUsers] = useState([]);
    const [selectedTaggedUsers, setSelectedTaggedUsers] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(undefined);
    const selectedTaggedUsersRef = useRef(selectedTaggedUsers);
    selectedTaggedUsersRef.current = selectedTaggedUsers;

    const usersById = useMemo(() => {
        return organisationUsers.reduce((acc, current) => {
            if (current?.userId) {
                acc[current.userId] = current;
            }
            return acc;
        }, {});
    }, [organisationUsers]);

    useEffect(() => {
        setNotes(Array.isArray(init) ? init : []);
    }, [init]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const users = await teal.getUsersInOrganisation(organisationId);
                const mapped = (users ?? []).map((u) => ({
                    userId: u.userId || u.id,
                    name: u.name || [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email,
                    email: u.email,
                })).filter((u) => !!u.userId);
                setOrganisationUsers(mapped);
            } catch (error) {
                setOrganisationUsers([]);
            }
        };

        if (organisationId) {
            fetchUsers();
        }
    }, [organisationId]);

    const updateNotesFromOrder = (order) => {
        const nextNotes = Array.isArray(order?.notes) ? order.notes : [];
        setNotes(nextNotes);
    };

    const handleSubmit = async (content) => {
        if (isSaving) return;
        if (!orderId) {
            setSnack({ message: 'Kunne ikke gemme note: mangler ordre-id' });
            return;
        }

        setIsSaving(true);
        const taggedUsers = normalizeTaggedUsers(selectedTaggedUsersRef.current);
        const isEditing = editingId !== undefined && editingId !== null;

        try {
            if (isEditing) {
                const result = await dispatch(editOrderNote(organisationId, orderId, editingId, {
                    content,
                    mentionTags: taggedUsers,
                }));

                if (result?.name === 'AxiosError') {
                    setSnack({ message: 'Besked kunne ikke opdateres' });
                    localStorage.setItem('PenlyAccessUserNotes', JSON.stringify(content));
                    return;
                }

                updateNotesFromOrder(result);
                setEditingId(undefined);
                setShowEditor(false);
                setSelectedTaggedUsers([]);
                localStorage.removeItem('PenlyAccessUserNotes');
                return;
            }

            const result = await dispatch(addOrderNote(organisationId, orderId, {
                content,
                mentionTags: taggedUsers,
            }));

            if (result?.name === 'AxiosError') {
                setSnack({ message: 'Besked kunne ikke sendes' });
                localStorage.setItem('PenlyAccessUserNotes', JSON.stringify(content));
                return;
            }

            updateNotesFromOrder(result);
            setShowEditor(false);
            setSelectedTaggedUsers([]);
            localStorage.removeItem('PenlyAccessUserNotes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (noteId) => {
        if (!orderId || !noteId || deletingId) return;

        setDeletingId(noteId);
        try {
            const result = await dispatch(deleteOrderNote(organisationId, orderId, noteId));
            if (result?.name === 'AxiosError') {
                setSnack({ message: 'Besked kunne ikke slettes' });
                return;
            }

            updateNotesFromOrder(result);

            if (editingId === noteId) {
                setEditingId(undefined);
                setShowEditor(false);
                setSelectedTaggedUsers([]);
                localStorage.removeItem('PenlyAccessUserNotes');
            }
        } finally {
            setDeletingId(undefined);
        }
    };

    const startEdit = (note) => {
        localStorage.setItem('PenlyAccessUserNotes', JSON.stringify(note.content || ''));
        setEditingId(getNoteId(note));
        setSelectedTaggedUsers(normalizeTaggedUsers(note.mentionTags));
        setShowEditor(true);
    };

    const cancelEdit = () => {
        setEditingId(undefined);
        setShowEditor(false);
        setSelectedTaggedUsers([]);
        localStorage.removeItem('PenlyAccessUserNotes');
    };

    return (
        <div sx={{
            width: '100%',
            backgroundColor: 'background.paper',
        }}>
            <p>Gem noter på Penly brugeren til dig selv og andre rådgivere i din virksomhed. Brugeren har ikke adgang til noterne.</p>
            <Snackbar
                open={ !!snack }
                autoHideDuration={ 6000 }
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={ () => setSnack(undefined) }
            >
                <MuiAlert severity={ snack?.severity || 'error' }>{ snack?.message }</MuiAlert>
            </Snackbar>

        

            <List>
                {notes.map((note) => {
                    const noteId = getNoteId(note);
                    const authorName = resolveNoteAuthorName(note, usersById, user?.fullName || '-');
                    const date = getNoteDate(note);

                    return (
                        <ListItem
                            key={ noteId }
                            sx={{
                                backgroundColor: editingId === noteId ? 'grey.300' : 'grey.200',
                                transition: 'background-color 150ms ease-in-out',
                                padding: 2,
                                marginBottom: 2,
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                <ListItemText
                                    primary={ authorName }
                                    secondary={ renderDateTime(date) }
                                    primaryTypographyProps={{ variant: 'h6' }}
                                    secondaryTypographyProps={{
                                        variant: 'body2',
                                    }}
                                />
                                <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>
                                    <Button
                                        size='small'
                                        variant='text'
                                        disabled={ deletingId === noteId || !orderId }
                                        onClick={ () => startEdit(note) }
                                    >
                                        Rediger
                                    </Button>
                                    <Button
                                        size='small'
                                        variant='text'
                                        color='error'
                                        disabled={ deletingId === noteId || !orderId }
                                        onClick={ () => handleDelete(noteId) }
                                    >
                                        {deletingId === noteId ? 'Sletter...' : 'Slet'}
                                    </Button>
                                </Box>
                            </Box>

                            <div
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.content || '') }}
                            />
                        </ListItem>
                    );
                })}
            </List>

            { (() => {
                const allTagged = collectAllTaggedUsers(notes);
                if (!allTagged.length) return null;
                return (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant='body2' color='text.secondary'>Informerede brugere:</Typography>
                        { allTagged.map(u => (
                            <Chip key={ u.userId } label={ u.name || u.email || u.userId } size='small' />
                        )) }
                    </Box>
                );
            })() }

            {
                showEditor ? (
                    <>
                        <Box sx={{ width: '100%' }}>
                            <TextEditor
                                key={ `penly-editor-${editingId ?? 'new'}` }
                                saveButtonText={ isSaving ? 'Gemmer...' : 'Gem' }
                                contentKey={ 'PenlyAccessUserNotes' }
                                onSubmit={ handleSubmit }
                                submitOnEnter={ false }
                            >
                                { (showEditor || selectedTaggedUsers.length > 0) && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                            <Typography variant='body2' color='text.secondary'>Informer Bruger</Typography>
                                            <Tooltip title='Brugeren modtager emails for alle nye noter.'>
                                                <InfoOutlinedIcon fontSize='small' color='action' />
                                            </Tooltip>
                                        </Box>
                                        <Autocomplete
                                            multiple
                                            options={ organisationUsers }
                                            value={ selectedTaggedUsers }
                                            onChange={ (_, value) => setSelectedTaggedUsers(normalizeTaggedUsers(value)) }
                                            getOptionLabel={ (option) => option?.name || option?.email || option?.userId || '' }
                                            isOptionEqualToValue={ (option, value) => option.userId === value.userId }
                                            sx={{ ml: 1, minWidth: 300 }}
                                            renderInput={ (params) => (
                                                <TextField
                                                    { ...params }
                                                    placeholder='Vælg en eller flere'
                                                    size='small'
                                                />
                                            ) }
                                        />
                                    </>
                                ) }
                            </TextEditor>
                        </Box>

                        {editingId && (
                            <Button variant='text' onClick={ cancelEdit }>Annuller</Button>
                        )}
                    </>
                ) : (
                    <Button
                        variant='outlined'
                        onClick={ () => {
                            localStorage.removeItem('PenlyAccessUserNotes');
                            setEditingId(undefined);
                            setSelectedTaggedUsers([]);
                            setShowEditor(true);
                        } }
                        color='primary'
                        disabled={ !orderId }
                    >
                        Ny note
                    </Button>
                )
            }

        </div>

    );
}
